import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { defineMessages, injectIntl } from 'react-intl';

import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';

import { startNewDirectMessage, submitComposeSuccess } from 'flavours/glitch/actions/compose';
import { expandConversations, expandConversationsRequest } from 'flavours/glitch/actions/conversations';
import { fetchStatus } from 'flavours/glitch/actions/statuses';
import api from 'flavours/glitch/api';
import { Icon } from 'flavours/glitch/components/icon';
import { IconButton } from 'flavours/glitch/components/icon_button';
import EditIcon from '@/material-icons/400-24px/edit.svg?react';
import ChatMessage from 'flavours/glitch/features/ui/components/chat_message';

import ConversationItem from './conversation_aside';

// Every open tab shares the same API token (300 requests / 5 min): poll
// rarely, and only while the tab is visible and the aside is expanded
const CONVERSATION_POLL_INTERVAL = 30000;

const messages = defineMessages({
  newDirectMessage: { id: 'chat_aside.new_direct_message', defaultMessage: 'Write a new direct message' },
});

/**
 * @class ChatAside
 * @augments {PureComponent}
 * @description ChatAside is a component that displays a chat aside of the main timeline. it allows to see a list of conversations and to compose a message to a specific conversation.
 */
class ChatAside extends PureComponent {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    conversations: ImmutablePropTypes.list,
    accounts: ImmutablePropTypes.map,
    loading: PropTypes.bool,
    hasMore: PropTypes.bool,
    statuses: ImmutablePropTypes.map,
    contexts: ImmutablePropTypes.map,
    compose: ImmutablePropTypes.map,
  };

  constructor(props) {
    super(props);
    this.state = {
      collapsed: true,
      activeConversation: null,
      message: '',
      in_reply_to_id: null,
      newMessages: 0,
      conversationLastMessage: null,
      conversationLastMessageBefore: null,
      conversationStatuses: [],

    };

    // Lier les méthodes au contexte this
    this.getAccountInfo = this.getAccountInfo.bind(this);
    this.handleOpenConversation = this.handleOpenConversation.bind(this);
    this.scrollDownMessages = this.scrollDownMessages.bind(this);
  }

  scrollDownMessages() {
    const messagesContent = document.getElementById('chat_aside_messages_content');
    if (messagesContent) {
      messagesContent.scrollTop = messagesContent.scrollHeight;
    }
  }

  toggleCollapsed = () => {
    this.setState(prevState => ({
      collapsed: !prevState.collapsed,
    }));
    if (!this.state.collapsed) {
      this.focusChatAsideCompose();
    }
  };
  focusChatAsideCompose = () => {
    const compose = document.querySelector('#compose_direct_chat_message');
    if (compose) {
      compose.focus();
    }
  };
  componentDidMount = () => {
    // Déclencher le chargement des conversations
    this.props.dispatch(expandConversations());
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    if (!this.state.collapsed) {
      this.focusChatAsideCompose();
    }

  };


  componentDidUpdate = (prevProps, prevState) => {
    // Réagir quand les conversations sont mises à jour après CONVERSATIONS_FETCH_SUCCESS

    // Sélectionner automatiquement la première conversation si aucune n'est active
    if (prevProps.conversations !== this.props.conversations && !this.state.activeConversation && this.props.conversations && this.props.conversations.size > 0) {
      const firstConversation = this.props.conversations.first();
      this.handleOpenConversation(firstConversation);
    }

    if (prevProps.statuses !== this.props.statuses && this.state.activeConversation) {
      this.updateConversationStatuses();
    }

    if (prevState.conversationStatuses !== this.state.conversationStatuses) {
      this.scrollDownMessages();
    }

    if (prevState.activeConversation !== this.state.activeConversation) {
      if (this.conversationPollInterval) {
        clearInterval(this.conversationPollInterval);
        this.conversationPollInterval = null;
      }
      if (this.state.activeConversation) {
        this.conversationPollInterval = setInterval(this.pollConversations, CONVERSATION_POLL_INTERVAL);
      }
    }

  };

  componentWillUnmount() {
    if (this.conversationPollInterval) {
      clearInterval(this.conversationPollInterval);
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  pollConversations = () => {
    if (document.hidden || this.state.collapsed || !this.state.activeConversation) return;

    this.props.dispatch(expandConversations());
    setTimeout(() => this.updateConversationStatuses(), 750);
  };

  // catch up at once when coming back to the tab
  handleVisibilityChange = () => {
    if (!document.hidden) this.pollConversations();
  };

  handleNewDirectMessage = () => {
    this.props.dispatch(startNewDirectMessage());
  };

  /**
   * envoyer un message à un utilisateur en réponse à un message
   * @param {Event} e - L'événement de soumission du formulaire
   */
  handleSubmitChatMessage = (e) => {
    e.preventDefault();

    const status = this.state.message;
    let in_reply_to_id = '';
    if (this.state.in_reply_to_id && this.state.message.length) {
      in_reply_to_id = this.state.in_reply_to_id;
    } else if (this.state.conversationLastMessageBefore) {
      in_reply_to_id = this.state.conversationLastMessageBefore.id;
    }
    if (!in_reply_to_id || !status) {
      this.props.dispatch(expandConversations());
      return;
    }

    const data = {
      status,
      in_reply_to_id: in_reply_to_id,
      media_ids: [],
      media_attributes: [],
      sensitive: false,
      spoiler_text: '',
      visibility: 'direct',
      language: 'fr',
    };

    api().request({
      url: '/api/v1/statuses', method: 'post', data, headers: {
        'Idempotency-Key': this.props.compose.get(['idempotencyKey']),
      },
    }).then((response) => {
      this.props.dispatch(submitComposeSuccess({ ...response.data }));
      this.props.dispatch(expandConversations());
      this.props.dispatch(expandConversationsRequest());
      this.setState(prevState => ({
        message: '',
        in_reply_to_id: response.data.id,
        conversationStatuses: [...prevState.conversationStatuses, response.data],
      }));

      this.handleOpenConversation(this.state.activeConversation);
      this.focusChatAsideCompose();
    }).catch(() => {
      // submission failed
    });
  };

  updateMessage = (e) => {
    this.setState({ message: e.target.value });
  };

  getAccountInfo(accountId) {
    const account = this.props.accounts.get(accountId);
    return account;
  }

  handleCloseConversation = () => {
    this.setState({ activeConversation: null });
  };

  getMessageInStore(statusId) {
    return this.props.statuses.get(statusId);
  }

  handleOpenConversation = (conversation) => {
    this.setState({
      activeConversation: conversation,
      in_reply_to_id: conversation.get('last_status'),
      conversationStatuses: [],
    }, () => {
      this.updateConversationStatuses();
    });
  };

  updateConversationStatuses = () => {
    if (this.state.activeConversation) {
      const { statuses, lastStatusId } = this.findAllStatusesInConversation(this.state.activeConversation);
      this.setState({
        conversationStatuses: statuses,
        in_reply_to_id: lastStatusId,
      });
    }
  };

  sendChatWithEnter = (e) => {
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      this.handleSubmitChatMessage(e);
    }
  };

  findAllStatusesInConversation = (conversation) => {
    if (!conversation || !conversation.get('last_status')) {
      return { statuses: [], lastStatusId: null };
    }

    const lastStatusId = conversation.get('last_status');
    const list_statuses = [];

    let lastStatus = this.getMessageInStore(lastStatusId);

    if (!lastStatus) {
      this.props.dispatch(fetchStatus(lastStatusId, false, false));
      return { statuses: [], lastStatusId };
    }

    list_statuses.push(lastStatus);

    let current = lastStatus;
    const max_fetch_messages = 50;
    let ii_loop = 0;

    while (current && current.get('in_reply_to_id') && ii_loop < max_fetch_messages) {
      const replyToId = current.get('in_reply_to_id');
      const previous = this.getMessageInStore(replyToId);
      if (!previous) {
        this.props.dispatch(fetchStatus(replyToId, false, false));
        break;
      }

      list_statuses.push(previous);
      current = previous;
      ii_loop++;
    }

    list_statuses.reverse();
    return { statuses: list_statuses, lastStatusId };
  };

  render() {
    const { conversations, loading } = this.props;
    const {
      collapsed, message, activeConversation, conversationStatuses,
    } = this.state;
    let activeInterlocuteur = null;
    if (activeConversation) {
      const interlocutorId = activeConversation.getIn(['accounts', 0]);
      if (interlocutorId) {
        activeInterlocuteur = this.getAccountInfo(interlocutorId);
      }
    }

    return (<div className={`chat-aside ${collapsed ? 'chat-aside--collapsed' : 'chat-aside--expanded'}`}>
      <div className='chat-aside__toggle'>
        <button
          className='chat-aside__header'
          onClick={this.toggleCollapsed}
        >
          <Icon id='envelope' icon='envelope' />
          Chat
          {activeInterlocuteur && (<>
            <span className='active-conversation-info'>
                avec

              <a href={activeInterlocuteur.get('url')}>
                <img src={activeInterlocuteur.get('avatar')} className='account__avatar' alt='avatar' />
                {activeInterlocuteur.get('username')}
              </a>

            </span>
            {conversationStatuses.length && (<span className='counter'>
              {conversationStatuses.length}

            </span>)}
          </>)}
        </button>
      </div>

      {!collapsed && (<div className='chat-aside__content'>
        <div className='chat-aside__conversations'>
          <h3 className='chat-aside__conversations-title'>

            <span className='badge chat-aside__conversations-counter'>
              {!loading && conversations && conversations.size}
            </span>
            <span>Conversations</span>
            <IconButton
              className='chat-aside__new-message'
              title={this.props.intl.formatMessage(messages.newDirectMessage)}
              icon='pencil'
              iconComponent={EditIcon}
              onClick={this.handleNewDirectMessage}
            />
          </h3>
          <div className='chat-aside__conversations-list'>
            {loading && (<div className='loading-indicator'>
              <div className='loading-indicator__figure' />
              Chargement
            </div>)}

            {!loading && conversations && conversations.size > 0 ? (<div className='conversations-list'>
              {conversations.map(conversation => (<ConversationItem
                key={conversation.get('id')}
                conversation={conversation}
                isActive={activeConversation && activeConversation.get('id') === conversation.get('id')}
                onOpenConversation={this.handleOpenConversation}
                getAccountInfo={this.getAccountInfo}
                dispatch={this.props.dispatch}
              />))}
            </div>) : (!loading && <p>Aucune conversation</p>)}
            <hr />
          </div>
        </div>

        {activeConversation && (<div className='chat-aside__messages'>
          <div className='chat-aside__messages-header'>
            <button onClick={this.handleCloseConversation}>
              fermer
            </button>
          </div>
          <div className='chat-aside__messages-content' id='chat_aside_messages_content'>
            {conversationStatuses.length > 0 && conversationStatuses.map((status) => {
              const statusId = status?.get ? status.get('id') : status?.id;
              if (!statusId) return null;

              return (
                <div key={'c_' + statusId} className='chat-message-container'>
                  <ChatMessage
                    status={status}
                    fromUs={false}
                  />
                </div>
              );
            })}

          </div>
          <div className='chat-aside__messages-input'>


            <form action='' onSubmit={this.handleSubmitChatMessage}>
              <textarea
                name='compose_direct_chat_message'
                id='compose_direct_chat_message'
                cols='30'
                rows='2'
                value={message}
                onChange={this.updateMessage}
                onKeyDown={this.sendChatWithEnter}
              />
              <button className='submit-button' type='submit'>📦 Envoyer</button>

            </form>
          </div>
        </div>)}
      </div>)}
    </div>);
  }
}

/**
 * Récupère les conversations et autres données du store Redux
 * @param {Object} state - L'état global Redux
 * @returns {Object} Les props mappées pour le composant
 */
const mapStateToProps = state => ({
  conversations: state.getIn(['conversations', 'items']),
  statuses: state.getIn(['statuses']),
  loading: state.getIn(['conversations', 'isLoading']),
  hasMore: state.getIn(['conversations', 'hasMore']),
  accounts: state.getIn(['accounts']),
  contexts: state.getIn(['contexts']),
  compose: state.getIn(['compose']),
});

/**
 * Mappe les actions Redux aux props du composant
 * @param {Function} dispatch - La fonction dispatch de Redux
 * @returns {Object} Les actions mappées pour le composant
 */
const mapDispatchToProps = dispatch => ({
  dispatch, onExpandConversations: () => dispatch(expandConversations()),
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ChatAside));
